import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "cookie-consent-v1";

export type CookieConsent = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  timestamp: string;
};

const PUBLIC_PATHS = ["/", "/welcome", "/terms", "/privacy", "/subprocessors", "/security"];

export function getCookieConsent(): CookieConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent;
    if (typeof parsed !== "object" || parsed === null) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent(): boolean {
  return getCookieConsent()?.analytics === true;
}

export function hasMarketingConsent(): boolean {
  return getCookieConsent()?.marketing === true;
}

function saveConsent(consent: Omit<CookieConsent, "timestamp">) {
  const payload: CookieConsent = {
    ...consent,
    necessary: true,
    timestamp: new Date().toISOString(),
  };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  window.dispatchEvent(new CustomEvent("cookie-consent-changed", { detail: payload }));
}

export function CookieConsent() {
  const [location] = useLocation();
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const onPublicPage = PUBLIC_PATHS.includes(location);
    if (!onPublicPage) {
      setVisible(false);
      return;
    }
    setVisible(getCookieConsent() === null);
  }, [location]);

  if (!visible) return null;

  const handleAcceptAll = () => {
    saveConsent({ necessary: true, analytics: true, marketing: true });
    setVisible(false);
  };

  const handleRejectAll = () => {
    saveConsent({ necessary: true, analytics: false, marketing: false });
    setVisible(false);
  };

  const handleSavePreferences = () => {
    saveConsent({ necessary: true, analytics, marketing });
    setShowPreferences(false);
    setVisible(false);
  };

  return (
    <>
      <div
        role="dialog"
        aria-live="polite"
        aria-label="Cookie consent"
        className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6"
      >
        <div className="mx-auto max-w-4xl rounded-2xl border border-gray-200 bg-white shadow-2xl">
          <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:gap-6 sm:p-6">
            <div className="hidden h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#0B1834]/10 text-[#0B1834] sm:flex">
              <Cookie className="h-5 w-5" />
            </div>
            <div className="flex-1 text-sm text-gray-700">
              <h2 className="mb-1 text-base font-semibold text-gray-900">We value your privacy</h2>
              <p className="leading-relaxed">
                We use cookies to make our site work and, with your consent, to analyse usage and
                improve your experience. You can accept all, reject non-essential cookies, or
                manage your preferences. Read more in our{" "}
                <Link href="/privacy" className="font-medium text-[#0B1834] underline hover:text-[#0A152E]">
                  Privacy Policy
                </Link>
                .
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-shrink-0 sm:flex-row sm:items-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const current = getCookieConsent();
                  setAnalytics(current?.analytics ?? false);
                  setMarketing(current?.marketing ?? false);
                  setShowPreferences(true);
                }}
                data-testid="button-cookie-preferences"
              >
                Manage Preferences
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRejectAll}
                data-testid="button-cookie-reject"
              >
                Reject
              </Button>
              <Button
                size="sm"
                className="bg-[#0B1834] text-white hover:bg-[#0A152E]"
                onClick={handleAcceptAll}
                data-testid="button-cookie-accept"
              >
                Accept
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showPreferences} onOpenChange={setShowPreferences}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Cookie preferences</DialogTitle>
            <DialogDescription>
              Choose which cookies you allow. Strictly necessary cookies are always on because the
              site cannot function without them.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-start justify-between gap-4 rounded-lg border border-gray-100 bg-gray-50 p-4">
              <div>
                <div className="font-medium text-gray-900">Strictly necessary</div>
                <p className="mt-1 text-sm text-gray-600">
                  Required for authentication, security, and core site features.
                </p>
              </div>
              <Switch checked disabled aria-label="Strictly necessary cookies (always on)" />
            </div>
            <div className="flex items-start justify-between gap-4 rounded-lg border border-gray-100 p-4">
              <div>
                <div className="font-medium text-gray-900">Analytics</div>
                <p className="mt-1 text-sm text-gray-600">
                  Help us understand how visitors use the site so we can improve it.
                </p>
              </div>
              <Switch
                checked={analytics}
                onCheckedChange={setAnalytics}
                aria-label="Analytics cookies"
                data-testid="switch-cookie-analytics"
              />
            </div>
            <div className="flex items-start justify-between gap-4 rounded-lg border border-gray-100 p-4">
              <div>
                <div className="font-medium text-gray-900">Marketing</div>
                <p className="mt-1 text-sm text-gray-600">
                  Used to deliver relevant content and measure campaign performance.
                </p>
              </div>
              <Switch
                checked={marketing}
                onCheckedChange={setMarketing}
                aria-label="Marketing cookies"
                data-testid="switch-cookie-marketing"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              variant="ghost"
              onClick={() => setShowPreferences(false)}
              className="hidden sm:inline-flex"
            >
              <X className="mr-1 h-4 w-4" />
              Cancel
            </Button>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button variant="outline" onClick={handleRejectAll} data-testid="button-cookie-reject-all">
                Reject all
              </Button>
              <Button
                className="bg-[#0B1834] text-white hover:bg-[#0A152E]"
                onClick={handleSavePreferences}
                data-testid="button-cookie-save"
              >
                Save preferences
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
